import { Info, AlertTriangle } from "lucide-react";

interface PaymentFeeNoticeProps {
    /** Nominal tagihan pokok (belum termasuk fee admin). Kolom `amount` di backend
     * bertipe decimal, jadi API sering mengirimnya sebagai STRING (mis. "939750000.00")
     * meski tipe di sini "number" - karena itu tetap di-coerce via Number() di bawah. */
    amount: number | string;
    /** true jika VA/rekening dalam USD (wire transfer internasional) */
    isUsd?: boolean;
    className?: string;
}

/**
 * Info biaya admin VA (untuk transfer beda bank) - dipakai di Dashboard & halaman
 * Status Pembayaran, konsisten dengan blok "Informasi Pembayaran" di halaman
 * conference-payment (ConferencePaymentPage.tsx).
 */
export function PaymentFeeNotice({ amount, isUsd = false, className = "" }: PaymentFeeNoticeProps) {
    const currency = isUsd ? "USD" : "IDR";
    const adminFee = isUsd ? 5 : 2500;
    // Number() wajib di sini: kalau `amount` datang sebagai string dari API (kasus decimal
    // Laravel), operator `+` dengan number akan menggabungkan string, bukan menjumlahkan
    // (mis. "939750000.00" + 2500 => "939750000.002500", lalu dibulatkan jadi sama seperti
    // semula saat diformat - itulah kenapa fee "hilang" dari total sebelumnya).
    const numericAmount = Number(amount) || 0;
    const totalWithFee = numericAmount + adminFee;

    const formatMoney = (val: number) =>
        new Intl.NumberFormat(isUsd ? "en-US" : "id-ID", {
            style: "currency",
            currency,
            minimumFractionDigits: 0,
        }).format(val);

    return (
        <div className={`space-y-4 ${className}`}>
            <div className={`flex items-center gap-2.5 ${isUsd ? "text-blue-700 dark:text-blue-400" : "text-emerald-700 dark:text-emerald-400"}`}>
                <Info className="w-4 h-4 shrink-0" />
                <h4 className="font-bold text-sm">
                    {isUsd ? "International / Non-BNI Transfer (USD)" : "Informasi Pembayaran (IDR)"}
                </h4>
            </div>

            <div className="bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/60 dark:border-amber-800/30 rounded-2xl p-4 sm:p-5 flex gap-3 shadow-sm">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div className="text-xs sm:text-sm text-amber-900 dark:text-amber-200/90 w-full">
                    <p className="font-bold mb-2">
                        {isUsd ? "Important Note: Wire Transfer Fees" : "Penting: Transfer Beda Bank"}
                    </p>
                    <p className="mb-3 leading-relaxed">
                        {isUsd ? (
                            <>
                                Please ensure the transfer is sent as{" "}
                                <strong className="font-extrabold text-amber-950 dark:text-amber-100">"Full Amount" (OUR)</strong>.
                                If not sent as Full Amount, an additional bank fee will be applied.
                            </>
                        ) : (
                            <>
                                Jika Anda transfer menggunakan bank <strong>selain BNI</strong>, wajib menginput{" "}
                                <strong>Nominal Tagihan Pokok + Fee Admin VA ({formatMoney(adminFee)})</strong> secara manual.
                            </>
                        )}
                    </p>

                    <div className="bg-white/80 dark:bg-slate-900/50 p-3.5 sm:p-4 rounded-xl border border-amber-100 dark:border-amber-900/50">
                        <p className="font-bold text-[10px] text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-widest">
                            {isUsd ? "Payment Simulation" : "Contoh Simulasi (Jika Beda Bank)"}
                        </p>
                        <ul className="space-y-1 font-mono text-xs sm:text-sm">
                            <li className="flex justify-between gap-4">
                                <span>{isUsd ? "Invoice Amount:" : "Nominal Tagihan:"}</span>
                                <span>{formatMoney(numericAmount)}</span>
                            </li>
                            <li className="flex justify-between gap-4 border-b border-amber-100 dark:border-amber-800/50 pb-1.5">
                                <span>{isUsd ? "Additional Bank Fee:" : "Fee Admin VA BNI:"}</span>
                                <span>{formatMoney(adminFee)} +</span>
                            </li>
                            <li className="flex justify-between gap-4 font-bold text-blue-700 dark:text-blue-400 pt-1.5">
                                <span>{isUsd ? "Total to Transfer:" : "Total Diinput:"}</span>
                                <span>{formatMoney(totalWithFee)}</span>
                            </li>
                        </ul>
                    </div>

                    {!isUsd && (
                        <p className="text-[10px] mt-2 opacity-70 italic leading-snug">
                            * Pembayaran menggunakan sesama bank BNI TIDAK perlu menginput nominal fee tambahan. Biaya transfer antar bank dikenakan terpisah oleh bank pengirim.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
